import gettext from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Row from '@sdk/components/ui/spacing/Row';
import H3 from '@sdk/components/ui/typography/H3';
import Text14 from '@sdk/components/ui/typography/Text14';
import Text16 from '@sdk/components/ui/typography/Text16';
import PickerField from '@sdk/components/forms/PickerField';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import type { CurrencyType } from '@sdk/common/currency/types/CurrencyType';

import { usePurchaseContext } from '@web/account/purchase/PurchaseProvider/PurchaseProvider';

import PurchaseColumn from '../PurchaseColumn';
import PaymentCycleAndMethod from '../PaymentCycleAndMethod/PaymentCycleAndMethod';
import { subscriptionLevelsPickerOptions } from '../../constants';

import { PaymentDetailsContainer, PaymentDetailsForm } from './paymentDetails.styles';

type Props = {
  currency: CurrencyType;
  monthlyPricePerUserPerMonth: number;
  yearlyPricePerUserPerMonth: number;
  yearlyPlanDiscount: number;
  areYearlySubscriptionsEnabled: boolean;
  isShopifyBilling: boolean;
  isOldPremium: boolean;
};

const PaymentDetails = ({
  currency,
  monthlyPricePerUserPerMonth,
  yearlyPricePerUserPerMonth,
  yearlyPlanDiscount,
  areYearlySubscriptionsEnabled,
  isShopifyBilling,
  isOldPremium,
}: Props) => {
  const {
    subscriptionLevel,
    setSubscriptionLevel,
    subscriptionBillingCycle,
    setSubscriptionBillingCycle,
  } = usePurchaseContext();
  const purchaseColumnProps: { isSingleColumn?: boolean; pBottom?: number } = {};

  if (isShopifyBilling) {
    purchaseColumnProps.isSingleColumn = true;
    purchaseColumnProps.pBottom = 0;
  }

  return (
    <PaymentDetailsContainer mRight={isShopifyBilling ? 0 : 20} autoWidth={isShopifyBilling}>
      <PurchaseColumn {...purchaseColumnProps}>
        <Row mBottom={5}>
          <H3 themeColor={ThemeColorType.primaryText}>{gettext('Payment details')}</H3>
        </Row>
        <Row>
          <Text14 themeColor={ThemeColorType.gray}>
            {isShopifyBilling
              ? gettext(
                  "If you decide a paid plan isn't for you, canceling is easy. The billing will be done by Shopify."
                )
              : gettext("If you decide a paid plan isn't for you, canceling is easy.")}
          </Text14>
        </Row>
        <Row mBottom={20} mTop={20}>
          <HorizontalSeparator />
        </Row>
        <PaymentDetailsForm autoWidth={isShopifyBilling}>
          {!isOldPremium ? (
            <>
              <Row>
                <Text16 semiBold themeColor={ThemeColorType.primaryText}>
                  {gettext('Plan')}
                </Text16>
              </Row>
              <Row mTop={10} mBottom={30}>
                <PickerField
                  options={subscriptionLevelsPickerOptions}
                  value={subscriptionLevel}
                  onChange={setSubscriptionLevel}
                />
              </Row>
            </>
          ) : null}
          {!isShopifyBilling ? (
            <PaymentCycleAndMethod
              currency={currency}
              monthlyPricePerUserPerMonth={monthlyPricePerUserPerMonth}
              yearlyPricePerUserPerMonth={yearlyPricePerUserPerMonth}
              yearlyPlanDiscount={yearlyPlanDiscount}
              subscriptionBillingCycle={subscriptionBillingCycle}
              setSubscriptionBillingCycle={setSubscriptionBillingCycle}
              areYearlySubscriptionsEnabled={areYearlySubscriptionsEnabled}
            />
          ) : null}
        </PaymentDetailsForm>
      </PurchaseColumn>
    </PaymentDetailsContainer>
  );
};

export default PaymentDetails;
